import { useRef, useState } from 'react';
import {
  BUILTIN_PRESETS,
  DEFAULT_SETTINGS,
  encodeSettings,
  type Preset,
  type QuantiseGrid,
  type Settings,
} from '../../core/settings';
import type { CcLane, DrumKind } from '../../core/types';
import {
  loadCustomPresets,
  presetFromJson,
  presetToJson,
  saveCustomPresets,
  withoutFileSpecific,
} from '../presets';
import { download } from '../util';
import { Group, NumberField, Select, Toggle } from './Controls';

interface Props {
  settings: Settings;
  onChange: (s: Settings) => void;
}

const CC_LANES: { lane: CcLane; label: string; hint: string }[] = [
  {
    lane: 'cutoff',
    label: 'Filter cutoff',
    hint: 'The 11-bit filter cutoff, on tracks whose voice is routed through the filter.',
  },
  { lane: 'resonance', label: 'Filter resonance', hint: 'The 4-bit filter resonance, on filtered voices.' },
  {
    lane: 'pulseWidth',
    label: 'Pulse width',
    hint: 'The 12-bit pulse width per voice – great for mapping to a PWM or timbre knob.',
  },
  {
    lane: 'waveform',
    label: 'Waveform',
    hint: 'Waveform bits (triangle 8, saw 16, pulse 32, noise 64 – summed for combined waves).',
  },
  { lane: 'volume', label: 'Master volume', hint: 'The SID master volume on every voice track.' },
];

const DRUMS: { kind: DrumKind; label: string }[] = [
  { kind: 'kick', label: 'Kick' },
  { kind: 'snare', label: 'Snare' },
  { kind: 'closedHat', label: 'Closed hi-hat' },
  { kind: 'openHat', label: 'Open hi-hat' },
  { kind: 'tom', label: 'Tom' },
];

const GRIDS: QuantiseGrid[] = ['off', '1/4', '1/8', '1/16', '1/32', '1/8T', '1/16T', '1/32T'];

function sameSettings(a: Settings, b: Settings) {
  return JSON.stringify(withoutFileSpecific(a)) === JSON.stringify(withoutFileSpecific(b));
}

export function ControlsPanel({ settings: s, onChange }: Props) {
  const [custom, setCustom] = useState<Preset[]>(() => loadCustomPresets());
  const [newName, setNewName] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const importRef = useRef<HTMLInputElement>(null);

  const set = <K extends keyof Settings>(k: K, v: Settings[K]) => onChange({ ...s, [k]: v });
  const setCc = (lane: CcLane, patch: Partial<Settings['cc'][CcLane]>) =>
    onChange({ ...s, cc: { ...s.cc, [lane]: { ...s.cc[lane], ...patch } } });

  const all = [...BUILTIN_PRESETS, ...custom];
  const activePreset = all.find((p) => sameSettings(p.settings, s));
  const flash = (m: string) => {
    setMessage(m);
    window.setTimeout(() => setMessage(null), 2500);
  };

  const applyPreset = (id: string) => {
    const p = all.find((x) => x.id === id);
    if (p) onChange({ ...p.settings, subtune: s.subtune });
  };

  const savePreset = () => {
    const name = newName.trim();
    if (!name) return;
    const p: Preset = {
      id: `c${Date.now()}`,
      name,
      description: 'Custom preset',
      settings: withoutFileSpecific(s),
    };
    const next = [...custom.filter((c) => c.name !== name), p];
    setCustom(next);
    saveCustomPresets(next);
    setNewName('');
    flash(`Saved preset “${name}”.`);
  };

  const deletePreset = (id: string) => {
    const next = custom.filter((c) => c.id !== id);
    setCustom(next);
    saveCustomPresets(next);
  };

  const shareLink = async () => {
    const url = new URL(window.location.href);
    const enc = encodeSettings(withoutFileSpecific(s));
    url.search = enc ? `?s=${enc}` : '';
    url.hash = '';
    try {
      await navigator.clipboard.writeText(url.toString());
      flash('Share link copied (settings only – never your SID file).');
    } catch {
      window.prompt('Copy this link:', url.toString());
    }
  };

  return (
    <section aria-labelledby="controls-heading" className="flex flex-col">
      <div className="flex flex-col gap-2 border-b border-line p-3">
        <div className="flex items-center justify-between">
          <h2 id="controls-heading" className="legend text-[13px]">
            Conversion
          </h2>
          <button
            type="button"
            className="btn"
            onClick={() => onChange({ ...DEFAULT_SETTINGS, subtune: s.subtune })}
          >
            Reset to defaults
          </button>
        </div>
        <label className="flex flex-col gap-1">
          <span className="label">Preset</span>
          <select
            className="field"
            value={activePreset?.id ?? ''}
            onChange={(e) => applyPreset(e.target.value)}
          >
            {!activePreset && <option value="">Custom settings</option>}
            <optgroup label="Built-in">
              {BUILTIN_PRESETS.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </optgroup>
            {custom.length > 0 && (
              <optgroup label="Saved">
                {custom.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </optgroup>
            )}
          </select>
          {activePreset && <span className="hint">{activePreset.description}</span>}
        </label>
        <div className="flex gap-1">
          <input
            className="field"
            placeholder="New preset name"
            aria-label="New preset name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && savePreset()}
          />
          <button type="button" className="btn" onClick={savePreset} disabled={!newName.trim()}>
            Save
          </button>
        </div>
        <div className="flex flex-wrap gap-1">
          {activePreset && custom.some((c) => c.id === activePreset.id) && (
            <button type="button" className="btn" onClick={() => deletePreset(activePreset.id)}>
              Delete preset
            </button>
          )}
          <button
            type="button"
            className="btn"
            onClick={() =>
              download(
                'sid2midi-preset.json',
                presetToJson(activePreset?.name ?? 'Custom', s),
                'application/json',
              )
            }
          >
            Export JSON
          </button>
          <button type="button" className="btn" onClick={() => importRef.current?.click()}>
            Import JSON
          </button>
          <button type="button" className="btn" onClick={() => void shareLink()}>
            Copy share link
          </button>
          <input
            ref={importRef}
            type="file"
            accept="application/json,.json"
            className="hidden"
            onChange={async (e) => {
              const f = e.target.files?.[0];
              e.target.value = '';
              if (!f) return;
              try {
                const { name, settings } = presetFromJson(await f.text());
                onChange({ ...settings, subtune: s.subtune });
                flash(`Imported “${name}”.`);
              } catch (err) {
                flash(err instanceof Error ? err.message : 'Could not import that file.');
              }
            }}
          />
        </div>
        <p role="status" aria-live="polite" className="min-h-[1rem] text-[11px] text-accent">
          {message}
        </p>
      </div>

      <Group title="General" defaultOpen>
        <Select
          label="Clock"
          hint="Auto uses the clock from the file header. Changing it re-runs the emulation."
          value={s.clock}
          options={[
            { value: 'auto', label: 'Auto (from header)' },
            { value: 'PAL', label: 'PAL (50 Hz, 985 kHz)' },
            { value: 'NTSC', label: 'NTSC (60 Hz, 1.02 MHz)' },
          ]}
          onChange={(v) => set('clock', v)}
        />
        <Select
          label="Duration"
          hint="Auto plays until the song loops or falls silent (max 10 minutes)."
          value={s.durationMode}
          options={[
            { value: 'auto', label: 'Auto-detect' },
            { value: 'fixed', label: 'Fixed length' },
          ]}
          onChange={(v) => set('durationMode', v)}
        />
        {s.durationMode === 'fixed' && (
          <NumberField
            label="Length"
            unit="seconds"
            hint="How long to capture (max 600 s)."
            value={s.durationSeconds}
            min={1}
            max={600}
            onChange={(v) => set('durationSeconds', v)}
          />
        )}
        <Toggle
          label="Loop detection"
          hint="Stop after the song repeats, so the MIDI contains exactly one pass."
          checked={s.loopDetection}
          onChange={(v) => set('loopDetection', v)}
        />
        <Select
          label="Tempo"
          hint="Auto estimates BPM from the player’s row timing."
          value={s.tempoMode}
          options={[
            { value: 'auto', label: 'Auto-estimate' },
            { value: 'manual', label: 'Manual BPM' },
          ]}
          onChange={(v) => set('tempoMode', v)}
        />
        {s.tempoMode === 'manual' && (
          <NumberField
            label="BPM"
            hint="Tempo written to the MIDI file."
            value={s.manualBpm}
            min={20}
            max={400}
            step={0.1}
            onChange={(v) => set('manualBpm', v)}
          />
        )}
        <Toggle
          label="Lock tempo to frame grid"
          hint="Adjusts the tempo so a 16th note is a whole number of frames – notes then sit exactly on the DAW grid."
          checked={s.frameGrid}
          onChange={(v) => set('frameGrid', v)}
        />
      </Group>

      <Group title="Notes">
        <NumberField
          label="New-note jump threshold"
          unit="semitones"
          hint="A pitch jump at least this big while the gate is held starts a new note (players often retrigger without the gate)."
          value={s.jumpThreshold}
          min={0.25}
          max={12}
          step={0.05}
          onChange={(v) => set('jumpThreshold', v)}
        />
        <NumberField
          label="Minimum note length"
          unit="frames"
          hint="Shorter notes are merged into the previous note or dropped."
          value={s.minNoteLength}
          min={1}
          max={16}
          onChange={(v) => set('minNoteLength', v)}
        />
        <Toggle
          label="Hard-restart detection"
          hint="Ignores the brief gate-off/ADSR reset many players do just before a note, so notes stay legato."
          checked={s.hardRestart}
          onChange={(v) => set('hardRestart', v)}
        />
        <Select
          label="Release"
          hint="Cut notes when the gate closes, or extend them by the SID release time."
          value={s.release}
          options={[
            { value: 'cut', label: 'Cut at gate-off' },
            { value: 'extend', label: 'Extend by release time' },
          ]}
          onChange={(v) => set('release', v)}
        />
        <NumberField
          label="Transpose (all)"
          unit="semitones"
          hint="Shifts every melodic note."
          value={s.transpose}
          min={-24}
          max={24}
          onChange={(v) => set('transpose', v)}
        />
        {[0, 1, 2].map((vi) => (
          <NumberField
            key={vi}
            label={`Transpose voice ${vi + 1}`}
            unit="semitones"
            hint={`Extra shift for voice ${vi + 1} only.`}
            value={s.voiceTranspose[vi]}
            min={-24}
            max={24}
            onChange={(v) => {
              const t = [...s.voiceTranspose] as Settings['voiceTranspose'];
              t[vi] = v;
              set('voiceTranspose', t);
            }}
          />
        ))}
      </Group>

      <Group title="Arpeggios">
        <Select
          label="Mode"
          hint="SID players fake chords by cycling notes every frame. Merge them into real chords, keep just the root, or keep the fast notes."
          value={s.arpMode}
          options={[
            { value: 'notes', label: 'Keep as fast notes' },
            { value: 'chord', label: 'Merge into chord' },
            { value: 'root', label: 'Root note only' },
          ]}
          onChange={(v) => set('arpMode', v)}
        />
        <NumberField
          label="Detection window"
          unit="frames"
          hint="Longest step (in frames) that still counts as part of an arpeggio."
          value={s.arpWindow}
          min={1}
          max={12}
          onChange={(v) => set('arpWindow', v)}
        />
        <NumberField
          label="Minimum repetitions"
          unit="cycles"
          hint="How many full cycles of the pattern are needed before it is treated as an arpeggio."
          value={s.arpMinReps}
          min={1}
          max={8}
          onChange={(v) => set('arpMinReps', v)}
        />
      </Group>

      <Group title="Vibrato and slides">
        <div className="notice-warn text-[12px]">
          Set your synth’s pitch-bend range to <strong>±{s.bendRange} semitones</strong> so bends play back
          correctly.
        </div>
        <NumberField
          label="Pitch-bend range"
          unit="semitones"
          hint="Written to the file as RPN 0. Must match the bend range of the synth you play the MIDI on."
          value={s.bendRange}
          min={1}
          max={24}
          onChange={(v) => set('bendRange', v)}
        />
        <Select
          label="Vibrato"
          hint="Output vibrato as pitch bend, follow it to the nearest note, or flatten it."
          value={s.vibratoMode}
          options={[
            { value: 'bend', label: 'Pitch bend' },
            { value: 'quantise', label: 'Quantise to nearest note' },
            { value: 'ignore', label: 'Ignore (flatten)' },
          ]}
          onChange={(v) => set('vibratoMode', v)}
        />
        <NumberField
          label="Vibrato depth threshold"
          unit="cents"
          hint="Pitch wobble smaller than this is flattened to the note."
          value={s.vibratoThreshold}
          min={0}
          max={100}
          onChange={(v) => set('vibratoThreshold', v)}
        />
        <Select
          label="Slides / portamento"
          hint="Output slides as a pitch-bend curve or as a run of separate notes."
          value={s.slideMode}
          options={[
            { value: 'bend', label: 'Pitch bend' },
            { value: 'notes', label: 'Separate notes' },
          ]}
          onChange={(v) => set('slideMode', v)}
        />
      </Group>

      <Group title="Drums">
        <Toggle
          label="Drum detection"
          hint="Finds drums made from noise bursts, rapid waveform switching and fast downward sweeps."
          checked={s.drumsEnabled}
          onChange={(v) => set('drumsEnabled', v)}
        />
        <Select
          label="Output"
          hint="Put drums on their own track (channel 10, General MIDI) or keep them on the voice that played them."
          value={s.drumOutput}
          options={[
            { value: 'separate', label: 'Separate drum track (ch. 10)' },
            { value: 'voice', label: 'Keep in voice track' },
          ]}
          onChange={(v) => set('drumOutput', v)}
        />
        <div className="grid grid-cols-2 gap-2">
          {DRUMS.map(({ kind, label }) => (
            <NumberField
              key={kind}
              label={label}
              hint={`MIDI note for ${label.toLowerCase()} hits.`}
              value={s.drumMap[kind]}
              min={0}
              max={127}
              slider={false}
              onChange={(v) => set('drumMap', { ...s.drumMap, [kind]: v })}
            />
          ))}
        </div>
      </Group>

      <Group title="Velocity">
        <Select
          label="Source"
          hint="Where note velocity comes from."
          value={s.velocitySource}
          options={[
            { value: 'fixed', label: 'Fixed value' },
            { value: 'sustain', label: 'Sustain level' },
            { value: 'sustainVolume', label: 'Sustain × master volume' },
            { value: 'attack', label: 'Attack time (fast = loud)' },
          ]}
          onChange={(v) => set('velocitySource', v)}
        />
        {s.velocitySource === 'fixed' ? (
          <NumberField
            label="Velocity"
            hint="Velocity for every note."
            value={s.fixedVelocity}
            min={1}
            max={127}
            onChange={(v) => set('fixedVelocity', v)}
          />
        ) : (
          <>
            <Select
              label="Curve"
              hint="Soft pushes velocities down, hard pushes them up."
              value={s.velocityCurve}
              options={[
                { value: 'linear', label: 'Linear' },
                { value: 'soft', label: 'Soft' },
                { value: 'hard', label: 'Hard' },
              ]}
              onChange={(v) => set('velocityCurve', v)}
            />
            <NumberField
              label="Minimum"
              hint="Velocity for the quietest notes."
              value={s.velocityMin}
              min={1}
              max={127}
              onChange={(v) => set('velocityMin', v)}
            />
            <NumberField
              label="Maximum"
              hint="Velocity for the loudest notes."
              value={s.velocityMax}
              min={1}
              max={127}
              onChange={(v) => set('velocityMax', v)}
            />
          </>
        )}
      </Group>

      <Group title="Quantisation">
        <Select
          label="Grid"
          hint="Snap note starts and ends to a musical grid at the song tempo."
          value={s.quantise}
          options={GRIDS.map((g) => ({ value: g, label: g === 'off' ? 'Off' : g }))}
          onChange={(v) => set('quantise', v)}
        />
        <NumberField
          label="Strength"
          unit="%"
          hint="How far notes move towards the grid."
          value={s.quantiseStrength}
          min={0}
          max={100}
          onChange={(v) => set('quantiseStrength', v)}
        />
        <NumberField
          label="Swing"
          unit="%"
          hint="Delays every second grid position for a shuffle feel."
          value={s.swing}
          min={0}
          max={100}
          onChange={(v) => set('swing', v)}
        />
      </Group>

      <Group title="Controllers">
        {CC_LANES.map(({ lane, label, hint }) => (
          <div key={lane} className="flex items-end gap-2">
            <div className="flex-1">
              <Toggle
                label={label}
                hint={hint}
                checked={s.cc[lane].enabled}
                onChange={(v) => setCc(lane, { enabled: v })}
              />
            </div>
            <label className="flex w-20 flex-col gap-1">
              <span className="label">CC #</span>
              <input
                type="number"
                className="field"
                min={0}
                max={127}
                value={s.cc[lane].cc}
                aria-label={`${label} controller number`}
                disabled={!s.cc[lane].enabled}
                onChange={(e) => {
                  const v = Number(e.target.value);
                  if (e.target.value !== '' && v >= 0 && v <= 127) setCc(lane, { cc: Math.round(v) });
                }}
              />
            </label>
          </div>
        ))}
        <NumberField
          label="Maximum CC rate"
          unit="events/s"
          hint="Thins controller and pitch-bend data so the DAW is not flooded."
          value={s.ccMaxRate}
          min={1}
          max={100}
          onChange={(v) => set('ccMaxRate', v)}
        />
      </Group>

      <Group title="Tracks and channels">
        <Toggle
          label="Merge voices into one track"
          hint="All three voices on a single MIDI track (each keeps its own channel 1–3)."
          checked={s.mergeVoices}
          onChange={(v) => set('mergeVoices', v)}
        />
        <Select
          label="Resolution"
          hint="MIDI ticks per quarter note."
          value={s.ppq}
          options={[96, 192, 240, 480, 960].map((v) => ({ value: v, label: `${v} PPQ` }))}
          onChange={(v) => set('ppq', v)}
        />
      </Group>
    </section>
  );
}
